import React, { useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField,
    Button,
    Paper,
    Typography,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import NavBar from "../navbar";
import { useNavigate } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";
import { fromUtcInput, toUtcInput } from "../utc";

const MAX_POINTS = 100000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const oneMonthFromNow = () => {
    const now = new Date();
    const limit = new Date(now);
    limit.setUTCDate(1);
    limit.setUTCMonth(limit.getUTCMonth() + 1);
    const lastDay = new Date(Date.UTC(limit.getUTCFullYear(), limit.getUTCMonth() + 1, 0)).getUTCDate();
    limit.setUTCDate(Math.min(now.getUTCDate(), lastDay));
    return limit;
};

const CreateGuestToken = ({ forUser = false }) => {
    const navigate = useNavigate();

    const [tokenData, setTokenData] = useState({
        name: "",
        points: 0,
        expires_at: "",
        user_id: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [newToken, setNewToken] = useState(null);
    const [copied, setCopied] = useState(false);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) {
        return validate_logged_in;
    }

    const handleChange = (field, value) => {
        setTokenData({ ...tokenData, [field]: value });
    };

    const validationError = (trimmedName, userId) => {
        if (forUser && !UUID_PATTERN.test(userId)) return "User ID must be a UUID";
        if (!trimmedName) return "Name is required";
        if (trimmedName.length > 100) return "Name must be at most 100 characters";
        if (tokenData.points < 0 || tokenData.points > MAX_POINTS) return `Points must be between 0 and ${MAX_POINTS}`;
        if (!tokenData.expires_at) return "Expires At is required";
        const expiresAt = new Date(tokenData.expires_at);
        if (expiresAt <= new Date()) return "Expires At must be in the future";
        if (expiresAt > oneMonthFromNow()) return "Expires At must be at most one month from now";
        return null;
    };

    const handleSave = async () => {
        const trimmedName = tokenData.name.trim();
        const userId = tokenData.user_id.trim();
        const problem = validationError(trimmedName, userId);
        if (problem) {
            setError(problem);
            return;
        }
        setError(null);
        setSaving(true);
        const body = { name: trimmedName, points: tokenData.points, expires_at: tokenData.expires_at };
        try {
            const response = await fetch(config.apiBaseUrl + (forUser ? "/admin/tokens/demo" : "/admin/tokens/guest"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify(forUser ? { ...body, user_id: userId } : body),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            const data = await response.json();
            if (forUser) {
                alert("Demo token created. The user will find it in the dashboard.");
                navigate("/tokens");
            } else {
                setNewToken(data.token);
            }
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(newToken);
            setCopied(true);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to copy token: ${err.message}`);
        }
    };

    const handleCloseDialog = () => {
        navigate("/tokens");
    };

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 600, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        {forUser ? "Create Demo Token for User" : "Create Guest Token"}
                    </Typography>
                    {error && (
                        <Typography variant="body2" color="error" gutterBottom>
                            {error}
                        </Typography>
                    )}
                    {forUser && (
                        <TextField
                            fullWidth
                            margin="normal"
                            required
                            label="User ID"
                            error={Boolean(error && error.startsWith("User ID"))}
                            value={tokenData.user_id}
                            onChange={(e) => handleChange("user_id", e.target.value)}
                        />
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        required
                        label="Name"
                        inputProps={{ maxLength: 100 }}
                        error={Boolean(error && error.startsWith("Name"))}
                        value={tokenData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label={`Points (max ${MAX_POINTS.toLocaleString()})`}
                        type="number"
                        inputProps={{ min: 0, max: MAX_POINTS }}
                        error={Boolean(error && error.startsWith("Points"))}
                        value={tokenData.points}
                        onChange={(e) =>
                            handleChange("points", Math.min(MAX_POINTS, Math.max(0, parseInt(e.target.value, 10) || 0)))
                        }
                    />
                    <Typography variant="body2" gutterBottom>
                        Expires At (UTC, at most one month ahead) *
                    </Typography>
                    <TextField
                        fullWidth
                        margin="normal"
                        required
                        type="datetime-local"
                        inputProps={{ min: toUtcInput(new Date().toISOString()), max: toUtcInput(oneMonthFromNow().toISOString()) }}
                        error={Boolean(error && error.startsWith("Expires At"))}
                        value={toUtcInput(tokenData.expires_at)}
                        onChange={(e) => handleChange("expires_at", fromUtcInput(e.target.value, ""))}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate(`/tokens`)}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            </Box>
            {newToken && (
                <Dialog open={true} disableEscapeKeyDown>
                    <DialogTitle>Token Created</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You will not be able to see this token again, please, save it and store carefully:
                        </DialogContentText>
                        <Box
                            sx={{
                                backgroundColor: "#f5f5f5",
                                padding: 2,
                                marginTop: 2,
                                borderRadius: 1,
                                wordBreak: "break-all",
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                {newToken}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCopy} color="secondary">
                            {copied ? "Copied" : "Copy"}
                        </Button>
                        <Button onClick={handleCloseDialog} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default CreateGuestToken;
